import { cn } from "@design-system/lib/utils";
import { useImageLoadFailed } from "@app/shared/hooks/use-image-load-failed.hook";

// Calm, low-saturation pairs (light bg / text, dark bg / text).
const AVATAR_TONES = [
  "bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-200",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200",
  "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  "bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-200",
  "bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-200",
  "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200",
];

/** First letters of the first two name parts (works for Arabic and Latin). */
export function getNameInitials(name?: string | null) {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);

  return parts
    .slice(0, 2)
    .map(part => Array.from(part)[0] ?? "")
    .join("")
    .toLocaleUpperCase();
}

function toneFor(seed: string | number | undefined, name?: string) {
  const source = String(seed ?? name ?? "");
  let hash = 0;

  for (let i = 0; i < source.length; i++) {
    hash = (hash * 31 + source.charCodeAt(i)) >>> 0;
  }

  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

type UserAvatarProps = {
  name?: string | null;
  imageUrl?: string | null;
  /** Used to derive a stable colour. */
  seed?: string | number;
  className?: string;
};

/**
 * Round avatar that falls back to an initials circle when there is no image
 * or the image fails to load (never shows the broken-image icon / alt text).
 */
export function UserAvatar({
  name,
  imageUrl,
  seed,
  className,
}: UserAvatarProps) {
  const image = useImageLoadFailed(imageUrl);
  const showImage = Boolean(imageUrl) && !image.failed;

  if (showImage) {
    return (
      <img
        src={imageUrl as string}
        alt=""
        aria-hidden="true"
        ref={image.ref}
        onError={image.onError}
        className={cn(
          "size-8 shrink-0 rounded-full object-cover ring-2 ring-white dark:ring-slate-800",
          className,
        )}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex size-8 shrink-0 select-none items-center justify-center rounded-full text-xs font-bold ring-2 ring-white dark:ring-slate-800",
        toneFor(seed, name ?? undefined),
        className,
      )}>
      {getNameInitials(name) || "?"}
    </span>
  );
}
